import { Injectable, inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateChildFn,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
    LocaleResolutionPolicy,
    LocaleResolutionResult,
} from '@app/common/locale/domain/entities/locale-resolution-policy';
import {
    LocaleErrorAction,
    LocaleErrorActionService,
} from '@app/common/locale/domain/services/locale-error-action.service';
import { DashboardLocaleStrategy } from '@app/common/locale/application/strategies/dashboard-locale.strategy';
import { LocaleResolutionService } from '@ui/features/locale/application/services/locale-resolution.service';
import { readLocalePolicy } from '@ui/features/locale/application/services/read-locale-policy';

const ERROR_PATH: string = '/error';

// canActivateChild corre antes de activar el componente de la ruta (a diferencia de NavigationEnd):
// resuelve el idioma segun la politica que declara la ruta.
@Injectable({ providedIn: 'root' })
export class ResolvePublicLocaleGuardService {
    private readonly localeErrorActionService: LocaleErrorActionService =
        new LocaleErrorActionService();

    public constructor(
        private readonly localeResolutionService: LocaleResolutionService,
        private readonly router: Router,
        private readonly dashboardLocaleStrategy: DashboardLocaleStrategy,
    ) {}

    public canActivateChild(
        _childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> {
        const policy: LocaleResolutionPolicy = readLocalePolicy(state.root);

        // El idioma del usuario lo posee DashboardLocaleStrategy. El bootstrap del dashboard carga el
        // idioma una sola vez por sesion, asi que en cada entrada a UserOnly se reaplica el ya cargado (no-op si aun no cargo).
        if (policy === LocaleResolutionPolicy.UserOnly) {
            this.dashboardLocaleStrategy.reapplyUserLocale();

            return of(true);
        }

        return this.localeResolutionService.resolve(state.url, policy).pipe(
            map((result: LocaleResolutionResult): boolean | UrlTree => {
                if (result.notFound) {
                    return this.toErrorPage(state.url);
                }

                // Respaldo cliente: el 301 real lo emite el server (server-redirects.ts).
                return result.redirectTo === null ? true : this.router.parseUrl(result.redirectTo);
            }),
            catchError((error: unknown): Observable<boolean | UrlTree> => {
                if (this.localeErrorActionService.resolve(error) === LocaleErrorAction.NotFound) {
                    return of(this.toErrorPage(state.url));
                }

                this.localeResolutionService.applyDefault();

                return of(true);
            }),
        );
    }

    // Evita el loop de redirects si ya estamos en /error.
    private toErrorPage(currentUrl: string): boolean | UrlTree {
        return currentUrl.split(/[?#]/)[0] === ERROR_PATH ? true : this.router.parseUrl(ERROR_PATH);
    }
}

export const resolvePublicLocaleGuard: CanActivateChildFn = (
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
    return inject(ResolvePublicLocaleGuardService).canActivateChild(childRoute, state);
};
